"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { toast } from "react-toastify";

const ThemeContext = createContext<any>(null);

/** مسیرهایی که با لایسنس منقضی هم باز می‌مونن */
const ALLOWED_WHEN_EXPIRED = [
  "/login",
  "/setup",
  "/userPanel/license",
  "/licenseError",
  "/api/dynamicOptions",
];

/** صفحه مخصوص کاربر عادی */
const PUBLIC_LICENSE_PAGE = "/licenseError";

/** صفحه مخصوص ادمین */
const ADMIN_LICENSE_PAGE = "/userPanel/license";

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const router = useRouter();

  const [theme, setTheme] = useState<string>("theme-light");
  const [load, setLoad] = useState(true);
  const [isOnline, setIsOnline] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [licenseExpired, setLicenseExpired] = useState(false);
  const [licenseExpiresAt, setLicenseExpiresAt] = useState<string | null>(null);

  const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isPathAllowed = useCallback((path: string) => {
    return ALLOWED_WHEN_EXPIRED.some(
      (allowed) => path === allowed || path.startsWith(allowed + "/")
    );
  }, []);

  /** تشخیص ادمین از روی مسیر (یا بعداً از session) */
  const isAdminArea = pathname.startsWith("/userPanel");

  const getRedirectPath = useCallback(() => {
    return isAdminArea ? ADMIN_LICENSE_PAGE : PUBLIC_LICENSE_PAGE;
  }, [isAdminArea]);

  const fetchTheme = useCallback(async () => {
    if (!isOnline) {
      if (errorTimeoutRef.current) return;
      errorTimeoutRef.current = setTimeout(() => {
        errorTimeoutRef.current = null;
      }, 5000);
      return;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);

      const res = await fetch("/api/dynamicOptions", {
        method: "GET",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
          "Cache-Control": "no-cache",
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      const data = await res.json();
      const options = Array.isArray(data) ? data[0] : data;

      // تم
      if (options?.siteTheme === "تاریک") {
        setTheme("theme-dark");
        document.querySelector("html")?.classList.add("theme-dark");
        document.querySelector("html")?.classList.remove("theme-light");
      } else {
        setTheme("theme-light");
        document.querySelector("html")?.classList.add("theme-light");
        document.querySelector("html")?.classList.remove("theme-dark");
      }

      // لایسنس
      const expiresAt = options?.licenseExpiresAt
        ? new Date(options.licenseExpiresAt)
        : null;

      setLicenseExpiresAt(expiresAt ? expiresAt.toISOString() : null);

      if (!expiresAt || expiresAt.getTime() < Date.now()) {
        setLicenseExpired(true);
      } else {
        setLicenseExpired(false);
      }

      setLoad(false);
      setRetryCount(0);
    } catch (error) {
      console.error("خطا در دریافت مقادیر داینامیک سایت", error);

      if (retryCount < 3) {
        setRetryCount((prev) => prev + 1);
        setTimeout(() => fetchTheme(), 2000);
        return;
      }

      setLoad(false);
    }
  }, [isOnline, retryCount]);

  // ریدایرکت بر اساس نوع کاربر
  useEffect(() => {
    if (load) return;
    if (!licenseExpired) return;
    if (isPathAllowed(pathname)) return;

    router.replace(getRedirectPath());
  }, [load, licenseExpired, pathname, isPathAllowed, router, getRedirectPath]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      toast.success("اتصال اینترنت برقرار شد. همگام‌سازی...", { autoClose: 3000 });
      fetchTheme();
    };

    const handleOffline = () => {
      setIsOnline(false);
      toast.warning("اتصال اینترنت قطع شد!", { autoClose: 5000 });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [fetchTheme]);

  useEffect(() => {
    fetchTheme();

    const interval = setInterval(() => {
      if (isOnline) fetchTheme();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchTheme, isOnline]);

  const toggleTheme = async () => {
    if (!isOnline) return;

    const newTheme = theme === "theme-light" ? "theme-dark" : "theme-light";
    const themeValue = newTheme === "theme-dark" ? "تاریک" : "روشن";

    setTheme(newTheme);
    document.querySelector("html")?.classList.remove(theme);
    document.querySelector("html")?.classList.add(newTheme);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const res = await fetch("/api/dynamicOptions", {
        method: "PUT",
        headers: { "Content-Type": "application/json; charset=utf-8" },
        body: JSON.stringify({ siteTheme: themeValue }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

      toast.success("تم با موفقیت تغییر کرد");
      await fetchTheme();
    } catch {
      const oldTheme = theme === "theme-light" ? "theme-dark" : "theme-light";
      setTheme(oldTheme);
      document.querySelector("html")?.classList.remove(newTheme);
      document.querySelector("html")?.classList.add(oldTheme);
      toast.error("خطا در تغییر تم. لطفاً مجدداً تلاش کنید.");
    }
  };

  if (load) {
    return <p className="ccdiv w-full h-[100vh]">در حال بارگذاری ...</p>;
  }

  // قفل UI برای مسیرهای غیرمجاز
  if (licenseExpired && !isPathAllowed(pathname)) {
    const redirectTo = getRedirectPath();

    return (
      <div className="ccdiv w-full h-[100vh] flex flex-col gap-4 text-center p-6">
        <h1 className="text-2xl font-bold">لایسنس منقضی شده است</h1>
        <p className="opacity-70">
          {isAdminArea
            ? "برای ادامه استفاده، لایسنس را تمدید کنید."
            : "سایت موقتاً در دسترس نیست. لطفاً بعداً مراجعه کنید."}
        </p>
        <button
          className="subBtn w-[200px] h-[45px]"
          onClick={() => router.replace(redirectTo)}
        >
          {isAdminArea ? "فعال‌سازی لایسنس" : "اطلاعات بیشتر"}
        </button>
      </div>
    );
  }

  return (
    <ThemeContext.Provider
      value={{
        theme,
        toggleTheme,
        isOnline,
        licenseExpired,
        licenseExpiresAt,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};